import React, { useEffect, useState } from "react";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CommandLineIcon,
  ServerStackIcon,
} from "@heroicons/react/24/outline";
import { Link, useLocation } from "react-router-dom";
import { NavigationEnvironment, productNavigation } from "./navigationConfig";

function environmentKey(productKey: string, environment: NavigationEnvironment) {
  return `${productKey}:${environment.key}`;
}

function Sidebar() {
  const { pathname } = useLocation();
  // Start collapsed on phone widths, the open sidebar would leave almost no room for the page
  const [isCollapsed, setIsCollapsed] = useState(() => window.matchMedia?.("(max-width: 767px)").matches ?? false);
  const activeEnvironment = productNavigation
    .flatMap((product) => product.environments.map((environment) => ({ product, environment })))
    .find(({ environment }) => pathname.startsWith(environment.basePath));
  const activeEnvironmentKey = activeEnvironment
    ? environmentKey(activeEnvironment.product.key, activeEnvironment.environment)
    : null;

  const [openEnvironments, setOpenEnvironments] = useState<Record<string, boolean>>(() => {
    if (!activeEnvironment) {
      return {};
    }

    return {
      [environmentKey(activeEnvironment.product.key, activeEnvironment.environment)]: true,
    };
  });

  useEffect(() => {
    if (!activeEnvironmentKey) {
      return;
    }

    setOpenEnvironments((current) => ({ ...current, [activeEnvironmentKey]: true }));
  }, [activeEnvironmentKey]);

  return (
    <aside
      className={`flex h-screen shrink-0 flex-col border-r border-[#c8c8c8] bg-slate-50 text-slate-900 transition-[width] duration-200 ease-out ${
        isCollapsed ? "w-14" : "w-fit min-w-[15rem] max-w-[20rem]"
      }`}
    >
      <div className={`flex h-11 items-center border-b border-slate-200 ${isCollapsed ? "justify-center px-2" : "justify-between px-4"}`}>
        {!isCollapsed && (
          <Link to="/" className="truncate text-[15px] font-bold tracking-[-0.01em] text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-500" aria-label="Go to Xuno Utilities home">
            Xuno Utilities
          </Link>
        )}
        <button
          type="button"
          onClick={() => setIsCollapsed((current) => !current)}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm text-slate-500 hover:bg-white hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-500"
        >
          {isCollapsed ? <ChevronRightIcon className="h-4 w-4" /> : <ChevronLeftIcon className="h-4 w-4" />}
        </button>
      </div>

      <nav className={`min-h-0 flex-1 overflow-y-auto ${isCollapsed ? "flex flex-col items-center space-y-2 px-3 py-3" : "px-3 py-3"}`} aria-label="Product navigation">
        {isCollapsed ? (
          productNavigation.map((product) => {
            return (
              <button
                key={product.key}
                type="button"
                onClick={() => setIsCollapsed(false)}
                aria-label={`Expand sidebar to view ${product.name} navigation`}
                title={product.name}
                className="flex h-7 w-7 items-center justify-center rounded-sm bg-transparent text-[10px] font-bold tracking-wide focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-500"
              >
                {product.logoPath ? (
                  <img src={product.logoPath} alt="" className="h-full w-full object-contain p-0.5" />
                ) : (
                  product.initials
                )}
              </button>
            );
          })
        ) : (
          productNavigation.map((product) => {
            return (
              <section key={product.key} className="border-b-2 border-slate-300 py-2.5 first:pt-0 last:border-b-0">
                <div className="grid grid-cols-[2.5rem,minmax(0,1fr)] items-center">
                  <div
                    className="flex h-7 w-7 shrink-0 justify-self-center items-center justify-center rounded-sm bg-transparent text-[10px] font-bold tracking-wide"
                    aria-label={`${product.name} logo slot`}
                  >
                    {product.logoPath ? (
                      <img src={product.logoPath} alt="" className="h-full w-full object-contain p-0.5" />
                    ) : (
                      product.initials
                    )}
                  </div>
                  <h2 className="truncate text-sm font-semibold text-slate-800">
                    {product.name}
                  </h2>
                </div>

                <div className="mt-1.5 space-y-0.5">
                  {product.environments.map((environment) => {
                    const key = environmentKey(product.key, environment);
                    const isActive = pathname.startsWith(environment.basePath);
                    const isOpen = Boolean(openEnvironments[key]);
                    const isDevelopment = environment.key === "development";
                    const activeEnvironmentClassName = isDevelopment ? "bg-[#e4e4e4] text-[#009b48]" : "bg-[#e4e4e4] text-[#e12b38]";
                    const Icon = isDevelopment ? CommandLineIcon : ServerStackIcon;

                    return (
                      <div key={key} className={`-mx-3 ${isActive ? "bg-[#e4e4e4]" : ""}`}>
                        <button
                          type="button"
                          onClick={() =>
                            setOpenEnvironments((current) => ({ ...current, [key]: !current[key] }))
                          }
                          aria-expanded={isOpen}
                          className={`relative grid w-full grid-cols-[2.5rem,minmax(0,1fr),auto] items-center px-3 py-1.5 text-left text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-500 ${
                            isActive
                            ? `${activeEnvironmentClassName} font-medium`
                            : "font-medium text-slate-700 hover:bg-[#e6e6e6] hover:text-slate-900"
                          }`}
                        >
                          <span className="flex h-4 items-center justify-center">
                            <Icon className={`h-4 w-4 ${isActive ? (isDevelopment ? "text-[#009b48]" : "text-[#e12b38]") : "text-slate-400"}`} />
                          </span>
                          <span className="min-w-0 truncate">
                            <span className="truncate">{environment.label}</span>
                          </span>
                          {isOpen ? (
                            <ChevronDownIcon className={`h-3 w-3 shrink-0 ${isActive ? "text-slate-500" : "text-slate-400"}`} />
                          ) : (
                            <ChevronRightIcon className={`h-3 w-3 shrink-0 ${isActive ? "text-slate-500" : "text-slate-400"}`} />
                          )}
                        </button>

                        {isOpen && (
                          <div className="pt-1">
                            {environment.features.length > 0 ? (
                              <div className="space-y-0.5">
                                {environment.features.map((feature) => {
                                  const featureMatchPath = feature.activePath ?? feature.path;
                                  const featureIsActive = pathname === featureMatchPath || pathname.startsWith(`${featureMatchPath}/`);

                                  return (
                                    <Link
                                      key={feature.path}
                                      to={feature.path}
                                      className={`grid grid-cols-[2.5rem,minmax(0,1fr)] items-center px-3 py-1.5 text-[13px] leading-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-500 ${
                                        featureIsActive
                                          ? "bg-[#d1d1d1] font-semibold text-slate-900"
                                          : "text-slate-700 hover:bg-[#dedede] hover:text-slate-900"
                                      }`}
                                    >
                                      <span className="flex justify-center">
                                        <span className={`h-1.5 w-1.5 rounded-full ${featureIsActive ? "bg-slate-900" : "bg-slate-400"}`} />
                                      </span>
                                      <span className="truncate">{feature.label}</span>
                                    </Link>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="grid grid-cols-[2.5rem,minmax(0,1fr)] px-3 py-1.5 text-xs text-slate-400"><span /><span>No features available.</span></p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })
        )}
      </nav>
    </aside>
  );
}

export default Sidebar;
